import { MakeLogicType, afterMount, connect, kea, path, selectors } from 'kea'
import { loaders } from 'kea-loaders'

import api from 'lib/api'
import { databaseTableListLogic } from 'scenes/data-management/database/databaseTableListLogic'

import { DatabaseSerializedFieldType } from '~/queries/schema/schema-general'
import { DataWarehouseViewLink, PropertyDefinition, PropertyType } from '~/types'

import type { DatabaseSchemaTable } from '../../../../../frontend/src/queries/schema/schema-general'

const TYPE_MAPPING: Partial<Record<DatabaseSerializedFieldType, PropertyType>> = {
    datetime: PropertyType.DateTime,
    string: PropertyType.String,
    integer: PropertyType.Numeric,
    float: PropertyType.Numeric,
    decimal: PropertyType.Numeric,
    boolean: PropertyType.Boolean,
    array: PropertyType.StringArray,
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface joinsLogicValues {
    allTablesMap: Record<string, DatabaseSchemaTable> // databaseTableListLogic
    columnsJoinedToPersons: PropertyDefinition[]
    joins: DataWarehouseViewLink[]
    joinsLoading: boolean
    personTableJoins: DataWarehouseViewLink[]
    tablesJoinedToPersons: {
        join: DataWarehouseViewLink
        table: DatabaseSchemaTable
    }[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface joinsLogicActions {
    loadJoins: () => any
    loadJoinsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadJoinsSuccess: (
        joins: DataWarehouseViewLink[],
        payload?: any
    ) => {
        joins: DataWarehouseViewLink[]
        payload?: any
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface joinsLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        personTableJoins: (joins: DataWarehouseViewLink[]) => DataWarehouseViewLink[]
        tablesJoinedToPersons: (
            allTablesMap: Record<string, DatabaseSchemaTable>,
            personTableJoins: DataWarehouseViewLink[]
        ) => {
            join: DataWarehouseViewLink
            table: DatabaseSchemaTable
        }[]
        columnsJoinedToPersons: (
            tablesJoinedToPersons: {
                join: DataWarehouseViewLink
                table: DatabaseSchemaTable
            }[]
        ) => PropertyDefinition[]
    }
}

export type joinsLogicType = MakeLogicType<joinsLogicValues, joinsLogicActions, Record<string, any>, joinsLogicMeta>

export const joinsLogic = kea<joinsLogicType>([
    path(['products', 'dataWarehouse', 'joinsLogic']),
    loaders({
        joins: [
            [] as DataWarehouseViewLink[],
            {
                loadJoins: async () => {
                    const joins = await api.dataWarehouseViewLinks.list()
                    return joins.results
                },
            },
        ],
    }),
    connect(() => ({
        values: [databaseTableListLogic, ['allTablesMap']],
    })),
    selectors({
        personTableJoins: [
            (s) => [s.joins],
            (joins: DataWarehouseViewLink[]) => joins.filter((join) => join.source_table_name === 'persons'),
        ],
        tablesJoinedToPersons: [
            (s) => [s.allTablesMap, s.personTableJoins],
            (
                allTablesMap: Record<string, import('~/queries/schema').DatabaseSchemaTable>,
                personTableJoins: DataWarehouseViewLink[]
            ) => {
                return personTableJoins.map((join: DataWarehouseViewLink) => {
                    // valid join should have a joining table name
                    const table = allTablesMap[join.joining_table_name as string]

                    return {
                        table,
                        join,
                    }
                })
            },
        ],
        columnsJoinedToPersons: [
            (s) => [s.tablesJoinedToPersons],
            (
                tablesJoinedToPersons: {
                    join: DataWarehouseViewLink
                    table: import('~/queries/schema').DatabaseSchemaTable
                }[]
            ) => {
                return tablesJoinedToPersons.reduce((acc, { table, join }) => {
                    if (table) {
                        acc.push(
                            ...Object.values(table.fields).map((column) => ({
                                id: `${join.field_name}.${column.name}`,
                                name: `${join.field_name}: ${column.name}`,
                                table: join.field_name,
                                property_type: TYPE_MAPPING[column.type] || PropertyType.String,
                            }))
                        )
                    }
                    return acc
                }, [] as PropertyDefinition[])
            },
        ],
    }),
    afterMount(({ actions }) => {
        actions.loadJoins()
    }),
])
